import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  writeBatch,
  Timestamp 
} from 'firebase/firestore';
import { calendarService } from './calendarService';

/**
 * Calendar Scheduling Service
 * 
 * Automatically populates and manages calendar events based on:
 * - Daily Decision (daily)
 * - V12 Diagnostic (monthly)
 * - Persistent Widgets / Weekly Actions (weekly)
 * - Course deadlines (user-set)
 * - Progress reports (scheduled)
 * 
 * Events are auto-generated when user sets up schedule and
 * marked complete when corresponding items are completed.
 */

// ============================================
// TRACKABLE EVENT DEFINITIONS
// ============================================

export const TRACKABLE_EVENTS = {
  dailyDecision: {
    id: 'dailyDecision',
    title: 'Daily Decision',
    description: 'Make your daily business decision to earn RSK points',
    type: 'task',
    category: 'daily',
    schedule: 'daily',
    color: '#4ECDC4', // Teal
    icon: '🎯',
    duration: 5, // minutes
    defaultTime: '09:00',
    priority: 'high',
  },
  v12Diagnostic: {
    id: 'v12Diagnostic',
    title: 'V12 Diagnostic',
    description: 'Complete your monthly business health assessment',
    type: 'task',
    category: 'diagnostic',
    schedule: 'monthly',
    color: '#FFE066', // Yellow
    icon: '🏎️',
    duration: 30,
    defaultTime: '10:00',
    dayOfMonth: 1, // First of each month
    priority: 'high',
  },
  tigerTeamBreakouts: {
    id: 'tigerTeamBreakouts',
    title: 'Tiger Team Breakouts',
    description: 'Review and update your Tiger Team progress',
    type: 'task',
    category: 'weekly',
    schedule: 'weekly',
    color: '#FF6B6B', // Coral Red
    icon: '🐅',
    duration: 20,
    defaultTime: '10:00',
    dayOfWeek: 1, // Monday
    priority: 'medium',
  },
  strategicAction: {
    id: 'strategicAction',
    title: 'Strategic Action Matrix',
    description: 'Update your strategic action items for the week',
    type: 'task',
    category: 'weekly',
    schedule: 'weekly',
    color: '#A855F7', // Purple
    icon: '⚡',
    duration: 15,
    defaultTime: '10:30',
    dayOfWeek: 1, // Monday
    priority: 'medium',
  },
  strategicDecision: {
    id: 'strategicDecision',
    title: 'Strategic Decision Matrix',
    description: 'Review and log your strategic decisions',
    type: 'task',
    category: 'weekly',
    schedule: 'weekly',
    color: '#F97316', // Orange
    icon: '🎲',
    duration: 15,
    defaultTime: '11:00',
    dayOfWeek: 1, // Monday
    priority: 'medium',
  },
  progressReportDaily: {
    id: 'progressReportDaily',
    title: 'Daily Progress Review',
    description: 'Review your daily progress report',
    type: 'reminder',
    category: 'report',
    schedule: 'daily',
    color: '#6B7280', // Gray
    icon: '📊',
    duration: 5,
    defaultTime: '17:00',
    priority: 'low',
  },
  progressReportWeekly: {
    id: 'progressReportWeekly',
    title: 'Weekly Progress Review',
    description: 'Review your weekly progress report',
    type: 'reminder',
    category: 'report',
    schedule: 'weekly',
    color: '#6B7280', // Gray
    icon: '📈',
    duration: 10,
    defaultTime: '17:00',
    dayOfWeek: 5, // Friday
    priority: 'low',
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the next occurrence of a specific day of week
 */
const getNextDayOfWeek = (date, dayOfWeek) => {
  const result = new Date(date);
  const currentDay = result.getDay();
  const daysUntil = (dayOfWeek - currentDay + 7) % 7;
  result.setDate(result.getDate() + (daysUntil === 0 ? 7 : daysUntil));
  return result;
};

/**
 * Get all occurrences of a day of week in a month
 */
const getDaysOfWeekInMonth = (year, month, dayOfWeek) => {
  const dates = [];
  const date = new Date(year, month, 1);
  
  // Find first occurrence
  while (date.getDay() !== dayOfWeek) {
    date.setDate(date.getDate() + 1);
  }
  
  // Get all occurrences in month
  while (date.getMonth() === month) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 7);
  }
  
  return dates;
};

/**
 * Get all days in a month
 */
const getDaysInMonth = (year, month) => {
  const dates = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    dates.push(new Date(year, month, day));
  }
  
  return dates;
};

/**
 * Set time on a date from a time string (HH:MM)
 */
const setTimeOnDate = (date, timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

/**
 * Generate a unique event ID for scheduled events
 */
const generateScheduledEventId = (userId, eventType, date) => {
  const dateStr = date.toISOString().split('T')[0];
  return `scheduled_${userId}_${eventType}_${dateStr}`;
};

// ============================================
// MAIN SERVICE
// ============================================

export const calendarSchedulingService = {
  /**
   * Get user's schedule preferences
   */
  async getUserSchedulePreferences(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { preferences: null, error: 'User not found' };
      }
      
      const userData = userDoc.data();
      return { 
        preferences: userData.schedulePreferences || this.getDefaultPreferences(),
        error: null 
      };
    } catch (error) {
      console.error('[CalendarScheduling] Error getting preferences:', error);
      return { preferences: null, error: error.message };
    }
  },

  /**
   * Get default schedule preferences
   */
  getDefaultPreferences() {
    return {
      enabled: true,
      timezone: 'America/Los_Angeles',
      dailyDecision: {
        enabled: true,
        time: '09:00',
      },
      v12Diagnostic: {
        enabled: true,
        time: '10:00',
        dayOfMonth: 1,
      },
      weeklyActions: {
        enabled: true,
        dayOfWeek: 1, // Monday
        tigerTeamBreakouts: { enabled: true, time: '10:00' },
        strategicAction: { enabled: true, time: '10:30' },
        strategicDecision: { enabled: true, time: '11:00' },
      },
      progressReports: {
        daily: { enabled: false, time: '17:00' },
        weekly: { enabled: true, time: '17:00', dayOfWeek: 5 },
      },
    };
  },

  /**
   * Save user's schedule preferences and sync calendar
   */
  async saveSchedulePreferences(userId, preferences) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        schedulePreferences: preferences,
        updatedAt: Timestamp.now(),
      });

      // Sync calendar with new preferences
      await this.syncCalendarForUser(userId, preferences);

      return { success: true, error: null };
    } catch (error) {
      console.error('[CalendarScheduling] Error saving preferences:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Generate events for a specific month based on preferences
   */
  generateEventsForMonth(year, month, preferences) {
    const events = [];
    
    if (!preferences?.enabled) return events;

    // Daily Decision events
    if (preferences.dailyDecision?.enabled) {
      const days = getDaysInMonth(year, month);
      const eventDef = TRACKABLE_EVENTS.dailyDecision;
      const time = preferences.dailyDecision.time || eventDef.defaultTime;
      
      days.forEach(date => {
        // Skip past dates
        if (date < new Date().setHours(0, 0, 0, 0)) return;
        
        events.push({
          ...eventDef,
          date: setTimeOnDate(date, time),
          time,
          scheduledEventType: 'dailyDecision',
        });
      });
    }

    // V12 Diagnostic (monthly)
    if (preferences.v12Diagnostic?.enabled) {
      const eventDef = TRACKABLE_EVENTS.v12Diagnostic;
      const dayOfMonth = preferences.v12Diagnostic.dayOfMonth || eventDef.dayOfMonth;
      const time = preferences.v12Diagnostic.time || eventDef.defaultTime;
      const date = new Date(year, month, dayOfMonth);
      
      // Only add if not in the past
      if (date >= new Date().setHours(0, 0, 0, 0)) {
        events.push({
          ...eventDef,
          date: setTimeOnDate(date, time),
          time,
          scheduledEventType: 'v12Diagnostic',
        });
      }
    }

    // Weekly Actions
    if (preferences.weeklyActions?.enabled) {
      const dayOfWeek = preferences.weeklyActions.dayOfWeek ?? 1;
      const weekDays = getDaysOfWeekInMonth(year, month, dayOfWeek);

      // Tiger Team Breakouts
      if (preferences.weeklyActions.tigerTeamBreakouts?.enabled) {
        const eventDef = TRACKABLE_EVENTS.tigerTeamBreakouts;
        const time = preferences.weeklyActions.tigerTeamBreakouts.time || eventDef.defaultTime;
        
        weekDays.forEach(date => {
          if (date < new Date().setHours(0, 0, 0, 0)) return;
          events.push({
            ...eventDef,
            date: setTimeOnDate(date, time),
            time,
            scheduledEventType: 'tigerTeamBreakouts',
          });
        });
      }

      // Strategic Action
      if (preferences.weeklyActions.strategicAction?.enabled) {
        const eventDef = TRACKABLE_EVENTS.strategicAction;
        const time = preferences.weeklyActions.strategicAction.time || eventDef.defaultTime;
        
        weekDays.forEach(date => {
          if (date < new Date().setHours(0, 0, 0, 0)) return;
          events.push({
            ...eventDef,
            date: setTimeOnDate(date, time),
            time,
            scheduledEventType: 'strategicAction',
          });
        });
      }

      // Strategic Decision
      if (preferences.weeklyActions.strategicDecision?.enabled) {
        const eventDef = TRACKABLE_EVENTS.strategicDecision;
        const time = preferences.weeklyActions.strategicDecision.time || eventDef.defaultTime;
        
        weekDays.forEach(date => {
          if (date < new Date().setHours(0, 0, 0, 0)) return;
          events.push({
            ...eventDef,
            date: setTimeOnDate(date, time),
            time,
            scheduledEventType: 'strategicDecision',
          });
        });
      }
    }

    // Progress Reports
    if (preferences.progressReports?.weekly?.enabled) {
      const eventDef = TRACKABLE_EVENTS.progressReportWeekly;
      const dayOfWeek = preferences.progressReports.weekly.dayOfWeek ?? 5;
      const time = preferences.progressReports.weekly.time || eventDef.defaultTime;
      const weekDays = getDaysOfWeekInMonth(year, month, dayOfWeek);

      weekDays.forEach(date => {
        if (date < new Date().setHours(0, 0, 0, 0)) return;
        events.push({
          ...eventDef,
          date: setTimeOnDate(date, time),
          time,
          scheduledEventType: 'progressReportWeekly',
        });
      });
    }

    return events;
  },

  /**
   * Sync calendar events for a user (creates/updates events for next 3 months)
   */
  async syncCalendarForUser(userId, preferences = null) {
    try {
      // Get preferences if not provided
      if (!preferences) {
        const { preferences: prefs, error } = await this.getUserSchedulePreferences(userId);
        if (error) return { success: false, error };
        preferences = prefs;
      }

      if (!preferences?.enabled) {
        console.log('[CalendarScheduling] Scheduling disabled for user');
        return { success: true, eventsCreated: 0 };
      }

      const now = new Date();
      const batch = writeBatch(db);
      let eventsCreated = 0;

      // Generate events for current month + next 2 months
      for (let i = 0; i < 3; i++) {
        const targetDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const year = targetDate.getFullYear();
        const month = targetDate.getMonth();

        const events = this.generateEventsForMonth(year, month, preferences);

        for (const event of events) {
          const eventId = generateScheduledEventId(userId, event.scheduledEventType, event.date);
          const eventRef = doc(db, 'events', eventId);

          // Check if event already exists
          const existingDoc = await getDoc(eventRef);
          
          // Only create if doesn't exist or not completed
          if (!existingDoc.exists() || existingDoc.data().status !== 'completed') {
            const eventData = {
              id: eventId,
              userId,
              title: event.title,
              description: event.description,
              type: event.type,
              category: event.category,
              color: event.color,
              icon: event.icon,
              date: Timestamp.fromDate(event.date),
              time: event.time,
              duration: event.duration,
              priority: event.priority,
              scheduledEventType: event.scheduledEventType,
              isScheduledEvent: true,
              status: 'upcoming',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            };

            batch.set(eventRef, eventData, { merge: true });
            eventsCreated++;
          }
        }
      }

      await batch.commit();
      console.log(`[CalendarScheduling] Created/updated ${eventsCreated} events for user ${userId}`);
      
      return { success: true, eventsCreated };
    } catch (error) {
      console.error('[CalendarScheduling] Error syncing calendar:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Mark a scheduled event as completed
   */
  async markScheduledEventComplete(userId, eventType, date = new Date()) {
    try {
      const eventId = generateScheduledEventId(userId, eventType, date);
      const eventRef = doc(db, 'events', eventId);
      
      const eventDoc = await getDoc(eventRef);
      
      if (eventDoc.exists()) {
        await updateDoc(eventRef, {
          status: 'completed',
          completedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        console.log(`[CalendarScheduling] Marked ${eventType} as completed for ${date.toDateString()}`);
        return { success: true };
      }
      
      return { success: true, message: 'Event not found (may not be scheduled)' };
    } catch (error) {
      console.error('[CalendarScheduling] Error marking event complete:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get today's scheduled events for a user
   */
  async getTodaysScheduledEvents(userId) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('isScheduledEvent', '==', true),
        where('date', '>=', Timestamp.fromDate(today)),
        where('date', '<', Timestamp.fromDate(tomorrow))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));

      // Sort by time
      events.sort((a, b) => a.date - b.date);

      return { events, error: null };
    } catch (error) {
      console.error('[CalendarScheduling] Error getting today\'s events:', error);
      return { events: [], error: error.message };
    }
  },

  /**
   * Get upcoming scheduled events for a user
   */
  async getUpcomingScheduledEvents(userId, days = 7) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + days);

      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('isScheduledEvent', '==', true),
        where('date', '>=', Timestamp.fromDate(today)),
        where('date', '<', Timestamp.fromDate(endDate))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));

      // Sort by date
      events.sort((a, b) => a.date - b.date);

      return { events, error: null };
    } catch (error) {
      console.error('[CalendarScheduling] Error getting upcoming events:', error);
      return { events: [], error: error.message };
    }
  },

  /**
   * Clear all scheduled events for a user (used when disabling scheduling)
   */
  async clearScheduledEvents(userId) {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('isScheduledEvent', '==', true),
        where('status', '==', 'upcoming')
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log(`[CalendarScheduling] Cleared ${snapshot.docs.length} scheduled events for user ${userId}`);
      
      return { success: true, eventsCleared: snapshot.docs.length };
    } catch (error) {
      console.error('[CalendarScheduling] Error clearing events:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Initialize scheduling for a new user (call during onboarding)
   */
  async initializeUserSchedule(userId) {
    try {
      // Set default preferences
      const defaultPrefs = this.getDefaultPreferences();
      await this.saveSchedulePreferences(userId, defaultPrefs);
      
      return { success: true, preferences: defaultPrefs };
    } catch (error) {
      console.error('[CalendarScheduling] Error initializing schedule:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get completion status for a specific date
   */
  async getCompletionStatusForDate(userId, date) {
    try {
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(targetDate);
      nextDay.setDate(nextDay.getDate() + 1);

      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('isScheduledEvent', '==', true),
        where('date', '>=', Timestamp.fromDate(targetDate)),
        where('date', '<', Timestamp.fromDate(nextDay))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const total = events.length;
      const completed = events.filter(e => e.status === 'completed').length;
      const pending = total - completed;

      return {
        total,
        completed,
        pending,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        events,
        error: null,
      };
    } catch (error) {
      console.error('[CalendarScheduling] Error getting completion status:', error);
      return { total: 0, completed: 0, pending: 0, completionRate: 0, events: [], error: error.message };
    }
  },
};

export default calendarSchedulingService;




